import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { fetchSetById } from '../gameSessionApi';

function isVideoUrl(url) {
  return typeof url === 'string' && /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url);
}

/** В сетке: видео только по hover, без звука */
function GridVideoThumb({ src, poster, active }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (active) {
      v.play().catch(() => {});
    } else {
      v.pause();
      try {
        v.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
  }, [active]);
  return (
    <video
      ref={ref}
      src={src}
      poster={poster || undefined}
      muted
      loop
      playsInline
      preload="metadata"
      disablePictureInPicture
      className="h-full w-full object-cover"
      onContextMenu={(e) => e.preventDefault()}
    />
  );
}

/**
 * @param {object} props
 * @param {string} props.setId
 */
export default function GameAlbumGallery({ setId }) {
  const [setDoc, setSetDoc] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [hoverVideoIndex, setHoverVideoIndex] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const d = await fetchSetById(setId);
        if (!cancel) setSetDoc(d);
      } catch {
        if (!cancel) setErr('Could not load album.');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [setId]);

  const items = Array.isArray(setDoc?.photos) ? setDoc.photos : [];

  useEffect(() => {
    if (lightbox == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowLeft') {
        setLightbox((i) => {
          if (i == null || !items.length) return i;
          const n = items.length;
          return ((i - 1) % n + n) % n;
        });
      }
      if (e.key === 'ArrowRight') {
        setLightbox((i) => {
          if (i == null || !items.length) return i;
          const n = items.length;
          return (i + 1) % n;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, items.length]);

  const step = (delta) => {
    setLightbox((i) => {
      if (i == null || !items.length) return i;
      const n = items.length;
      return ((i + delta) % n + n) % n;
    });
  };

  if (loading) {
    return <div className="p-8 text-center text-sm text-game-muted">Loading…</div>;
  }
  if (err) {
    return <div className="p-8 text-center text-sm text-red-400">{err}</div>;
  }

  return (
    <>
      <div className="p-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
          {items.map((ph, i) => {
            const thumb = ph.thumbnailUrl || ph.imageUrl;
            const vid = isVideoUrl(ph.imageUrl);
            return (
              <button
                type="button"
                key={ph._id || i}
                onClick={() => setLightbox(i)}
                onContextMenu={(e) => e.preventDefault()}
                onMouseEnter={() => {
                  if (vid) setHoverVideoIndex(i);
                }}
                onMouseLeave={() => {
                  if (vid) setHoverVideoIndex((h) => (h === i ? null : h));
                }}
                className="relative aspect-square overflow-hidden rounded-xl border border-white/10 bg-game-surface"
              >
                {thumb && !vid ? (
                  <img
                    src={thumb}
                    alt=""
                    className="h-full w-full object-cover"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                ) : vid ? (
                  <GridVideoThumb
                    src={ph.imageUrl}
                    poster={ph.thumbnailUrl || undefined}
                    active={hoverVideoIndex === i}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-game-muted">?</div>
                )}
              </button>
            );
          })}
        </div>
        {items.length === 0 ? (
          <p className="mt-4 text-center text-xs text-game-muted">No media in this set.</p>
        ) : null}
      </div>

      <AnimatePresence>
        {lightbox != null && items[lightbox] ? (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            <button
              type="button"
              className="absolute right-4 top-4 z-10 rounded-lg bg-white/10 px-3 py-1 text-sm text-white"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(null);
              }}
            >
              Close
            </button>
            {items.length > 1 ? (
              <>
                <button
                  type="button"
                  className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-lg bg-white/10 px-2 py-3 text-white md:left-4"
                  onClick={(e) => {
                    e.stopPropagation();
                    step(-1);
                  }}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-lg bg-white/10 px-2 py-3 text-white md:right-4"
                  onClick={(e) => {
                    e.stopPropagation();
                    step(1);
                  }}
                >
                  ›
                </button>
              </>
            ) : null}
            <div
              className="relative max-h-[85vh] max-w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {isVideoUrl(items[lightbox].imageUrl) ? (
                <video
                  key={items[lightbox]._id}
                  src={items[lightbox].imageUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                  disablePictureInPicture
                  controls={false}
                  className="pointer-events-none max-h-[85vh] max-w-full select-none rounded-lg object-contain"
                  onContextMenu={(e) => e.preventDefault()}
                />
              ) : (
                <img
                  key={items[lightbox]._id}
                  src={items[lightbox].imageUrl}
                  alt=""
                  draggable={false}
                  className="max-h-[85vh] max-w-full select-none rounded-lg object-contain"
                  onContextMenu={(e) => e.preventDefault()}
                />
              )}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}
